import { useEffect, useRef, useState, type ComponentProps } from "react";
import type { Integration } from "../../../types/integration";
import type {
  DashboardConcierge,
  DashboardInstance,
  DashboardSpend,
} from "../mock";
import { ActiveIntegrations } from "../../../components/dashboard/hero/ActiveIntegrations";
import { AiSettingsModal } from "../../../components/dashboard/AiSettingsModal";
import { ChatGptSwitchModal } from "../../../components/dashboard/ChatGptSwitchModal";
import { ConciergePanel } from "../../../components/dashboard/hero/ConciergePanel";
import { RequiredAppsChecklist } from "../../../components/dashboard/hero/RequiredAppsChecklist";
import { IdentityBar } from "../../../components/dashboard/hero/IdentityBar";
import { KpiStrip } from "../../../components/dashboard/hero/KpiStrip";
import { RestartAgentModal } from "../../../components/dashboard/RestartAgentModal";
import { ShareInstanceModal } from "../../../components/dashboard/ShareInstanceModal";
import { UpdateOpenRouterKeyModal } from "../../../components/dashboard/UpdateOpenRouterKeyModal";
import { OnboardingVideo } from "../../../components/dashboard/OnboardingVideo";
import { useChatGptLink } from "../../../hooks/useChatGptLink";
import { bookingBlocked, type RequiredApp } from "../../../lib/requiredApps";
import styles from "./InstanceHero.module.css";

type Props = {
  instance: DashboardInstance;
  spend: DashboardSpend | null;
  concierge: DashboardConcierge;
  activeIntegrations: ReadonlyArray<Integration>;
  // Apps an admin pinned to this customer's invite (IP-588), joined to what
  // this tenant has connected. Surfaced on the locked screen — that's where
  // they land while setup finishes, and where the onboarding-call CTA lives.
  requiredApps: ReadonlyArray<RequiredApp>;
  // False while the data behind `requiredApps` is still loading, so an empty
  // list can't be mistaken for "nothing left to connect" (see requiredApps).
  requiredAppsResolved: boolean;
  // Refetch hook for the metrics powering the AI credits cell; called
  // after a successful OpenRouter key swap.
  onAiKeyUpdated?: () => void;
  // Called after the AI provider or billed account changes so the spend cell
  // re-reads. The `chatgptLinked` flag itself needs no prompting — it rides on
  // the instance list, which the dashboard SSE stream re-polls every few seconds.
  onProviderChanged?: () => void;
  toolCalls?: ComponentProps<typeof KpiStrip>["toolCalls"];
};

export const InstanceHero = ({
  instance,
  spend,
  concierge,
  activeIntegrations,
  requiredApps,
  requiredAppsResolved,
  onAiKeyUpdated,
  onProviderChanged,
  toolCalls,
}: Props): JSX.Element => {
  const isLocked = instance.locked === true;
  const isProvisioning = instance.status === "provisioning";
  const bookCallUrl = concierge.bookCallUrl;
  const hasBookCallUrl = bookCallUrl.length > 0;
  // The call is only useful once the apps we asked for are connected — we
  // spend it wiring them up. Apps the customer can't action never block (see
  // lib/requiredApps), so this can't strand anyone.
  const callBlocked = bookingBlocked(requiredApps, requiredAppsResolved);
  const bookCallLabel = "Book your onboarding call";
  const hasRequiredApps = requiredApps.length > 0;
  // Blocked for two different reasons, and they don't read the same: one asks
  // the customer to do something, the other admits we don't know yet.
  const blockedTitle = requiredAppsResolved
    ? "Connect the apps above first"
    : "Checking which apps still need connecting";
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const [keyModalOpen, setKeyModalOpen] = useState<boolean>(false);
  const [restartOpen, setRestartOpen] = useState<boolean>(false);
  const [chatgptOpen, setChatgptOpen] = useState<boolean>(false);
  const [aiSettingsOpen, setAiSettingsOpen] = useState<boolean>(false);
  // Only the instance owner manages collaborators (no manager role surfaced).
  const canShare = instance.sharingEnabled === true && instance.role === "OWNER";
  // Owner or MANAGER can rotate the OpenRouter key (it changes whose
  // account is billed, so plain MEMBERs don't get the gear).
  const canConfigureKey =
    instance.openrouterKeyConfigurable === true && instance.role !== "MEMBER";
  // Owner or MANAGER can restart the agent (it briefly interrupts the shared
  // instance, so a plain MEMBER doesn't get the button). Backend re-checks.
  const canRestart = instance.role !== "MEMBER";
  // Same posture as the OpenRouter key: switching AI provider changes whose
  // account pays for every agent turn, so plain MEMBERs don't get the prompt.
  // Provisioning is excluded too — there's no instance to reconfigure yet.
  const canSwitchProvider =
    instance.chatgptSelfServeEnabled === true &&
    instance.role !== "MEMBER" &&
    !isProvisioning;
  // The cog is the single entry point for the AI cell, so it shows whenever
  // any one of the actions behind it is available.
  const canOpenAiSettings = canConfigureKey || canSwitchProvider;
  // Which ChatGPT account is billed reaches the KPI strip from our tenant row,
  // and that row lags a switch by the length of an instance read (~36s in
  // prod). Showing the previous address as settled through that window read as
  // "the switch failed" to an operator who had just changed it.
  //
  // So the dialog hands the new account down as it reports the change — it has
  // just been told it by the box or by the endpoint that did the reordering —
  // and the cell shows the right address straight away. Kept with the instance
  // it came from: the tab strip can move to another tenant under us, and this
  // is only true of one of them.
  const [billedAccount, setBilledAccount] = useState<
    { instanceId: string; email: string } | null
  >(null);
  // Only for the paths that genuinely can't say who pays. There we do go and
  // ask — one read, never polled — and hold a placeholder for its duration
  // rather than presenting the row's previous address as settled.
  const [accountChecking, setAccountChecking] = useState<boolean>(false);
  const { loadProvider } = useChatGptLink(instance.id);
  // The dialog can report a second change while a read from the first is still
  // out (the open-time reconcile, then a "Make primary"), and the earlier one
  // returning would otherwise clear the placeholder onto a row it never
  // corrected — the stale-as-settled value this exists to prevent. Only the
  // newest report clears it.
  const providerReadSeq = useRef<number>(0);
  const handleProviderChanged = (account: string | null): void => {
    onProviderChanged?.();
    const seq = providerReadSeq.current + 1;
    providerReadSeq.current = seq;
    if (account !== null) {
      setBilledAccount({ instanceId: instance.id, email: account });
      setAccountChecking(false);
      return;
    }
    setBilledAccount(null);
    setAccountChecking(true);
    void loadProvider().finally(() => {
      if (providerReadSeq.current === seq) setAccountChecking(false);
    });
  };
  const accountOverride =
    billedAccount !== null && billedAccount.instanceId === instance.id
      ? billedAccount.email
      : null;
  // Stand in only until the tenant row says the same thing (next claws
  // snapshot, ~3s). Holding it past that would mask a later change made
  // anywhere else with a value that is no longer the newest one we have.
  useEffect(() => {
    if (
      billedAccount !== null &&
      billedAccount.instanceId === instance.id &&
      instance.chatgptAccount === billedAccount.email
    ) {
      setBilledAccount(null);
    }
  }, [billedAccount, instance.id, instance.chatgptAccount]);

  // While our team finishes setup, the whole hero is replaced by the setup
  // card: walkthrough video, status line and the onboarding-call CTA.
  if (isLocked) {
    return (
      <section className={styles.hero} role="status" aria-live="polite">
        <div className={styles.glow} aria-hidden="true" />
        <div className={styles.lockedContent}>
          <div
            className={
              hasRequiredApps ? `${styles.lockedSplit} ${styles.lockedSplitTop}` : styles.lockedSplit
            }
          >
            <OnboardingVideo />
            <div className={styles.lockedText}>
              <span className={styles.lockedEyebrow}>// In setup</span>
              <h2 className={styles.lockedTitle}>We're setting up {instance.name}</h2>
              {hasRequiredApps ? (
                <>
                  <p className={styles.lockedBody}>
                    We'll unlock your agent before your onboarding call. Until then, watch
                    the walkthrough and connect these apps so we can put them to work on
                    the call.
                  </p>
                  <RequiredAppsChecklist apps={requiredApps} tenantId={instance.id} />
                </>
              ) : (
                <>
                  <p className={styles.lockedBody}>
                    Your agent is being set up in the background, and we'll unlock it
                    before your onboarding call. In the meantime, watch the walkthrough
                    and book your call.
                  </p>
                  <p className={styles.lockedBody}>
                    Want a head start? Connect your integrations from the dashboard
                    now. We'll set up your messaging channels with you on the call.
                  </p>
                </>
              )}
              {hasBookCallUrl ? (
                callBlocked ? (
                  <button
                    type="button"
                    className={styles.lockedCta}
                    disabled
                    title={blockedTitle}
                  >
                    {bookCallLabel}
                    <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <a
                    className={styles.lockedCta}
                    href={bookCallUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {bookCallLabel}
                    <span aria-hidden="true">→</span>
                  </a>
                )
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.hero} aria-labelledby="dashboard-hero-title">
      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.content}>
        <IdentityBar
          instance={instance}
          {...(canShare ? { onShare: () => setShareOpen(true) } : {})}
        />

        {/* One cog for the whole AI cell. It only appears when there is at
            least one thing the caller may actually change. */}
        <KpiStrip
          instance={instance}
          spend={spend}
          accountChecking={accountChecking}
          accountOverride={accountOverride}
          {...(toolCalls !== undefined ? { toolCalls } : {})}
          {...(canOpenAiSettings
            ? { onOpenAiSettings: () => setAiSettingsOpen(true) }
            : {})}
        />

        <div className={styles.split}>
          <div className={styles.leftColumn}>
            <ActiveIntegrations integrations={activeIntegrations} />
          </div>
          <div className={styles.rightColumn}>
            <ConciergePanel
              concierge={concierge}
              tenantId={instance.id}
              {...(canRestart
                ? {
                    onRestart: () => setRestartOpen(true),
                    restartDisabled: isProvisioning,
                  }
                : {})}
            />
          </div>
        </div>
      </div>

      {shareOpen ? (
        <ShareInstanceModal
          tenantId={instance.id}
          instanceName={instance.name}
          callerCanManage={instance.role === "OWNER"}
          onClose={() => setShareOpen(false)}
        />
      ) : null}

      {keyModalOpen ? (
        <UpdateOpenRouterKeyModal
          tenantId={instance.id}
          instanceName={instance.name}
          onClose={() => setKeyModalOpen(false)}
          onApplied={() => onAiKeyUpdated?.()}
        />
      ) : null}

      {restartOpen ? (
        <RestartAgentModal
          tenantId={instance.id}
          instanceName={instance.name}
          onClose={() => setRestartOpen(false)}
        />
      ) : null}

      {aiSettingsOpen ? (
        <AiSettingsModal
          instanceName={instance.name}
          onChatGpt={instance.aiMode === "chatgpt" || instance.chatgptLinked === true}
          onClose={() => setAiSettingsOpen(false)}
          {...(canConfigureKey
            ? {
                onUpdateOpenRouterKey: () => {
                  // Close before opening the target: two ModalShells at once
                  // would stack backdrops and trap focus between them.
                  setAiSettingsOpen(false);
                  setKeyModalOpen(true);
                },
              }
            : {})}
          {...(canSwitchProvider
            ? {
                onOpenChatGpt: () => {
                  setAiSettingsOpen(false);
                  setChatgptOpen(true);
                },
              }
            : {})}
        />
      ) : null}

      {chatgptOpen ? (
        <ChatGptSwitchModal
          tenantId={instance.id}
          instanceName={instance.name}
          linked={instance.chatgptLinked === true}
          aiMode={instance.aiMode ?? "openrouter"}
          onClose={() => setChatgptOpen(false)}
          onChanged={handleProviderChanged}
        />
      ) : null}
    </section>
  );
};
